// Stable Y-axis registry (standing product rule, recorded 2026-08-09): once a
// chart has drawn a scale, entity ticking, unticking, and drilling must never
// move it. Each chart keeps a high-water mark of the largest value it has ever
// plotted under its key; narrowing the selection shrinks the bars, never the
// axis. The mark only rises when new data genuinely exceeds it (widening a
// selection that started narrow).
//
// Marks are keyed per filter context, per chart, per displayed dimension, per
// view mode. The context fingerprint (frameworks plus reporting periods,
// computed from the live filters, not from a response) is part of the key
// instead of a reset call: a filter change reads fresh marks on its very next
// render, with no reset-versus-render ordering to race and nothing to miss
// when a context's totals request is slow or fails. Old contexts' marks
// become inert entries, bounded by how many filter combinations a session
// visits. The business-unit and country views are different charts, as are
// per-entity and consolidated, so each keeps its own stable scale. A drill
// shares its parent view's key on purpose: children are portions of the
// parent bar and must stay on the parent's scale.
//
// Plain module state, not MobX: the mark is read and raised during render
// from the same data that triggered the render, so no independent re-render
// path ever depends on it. Raising during render is idempotent and monotonic,
// which makes StrictMode double renders safe. A discarded concurrent render
// could in principle leave a raised mark behind; this app schedules no
// concurrent renders (no transitions or suspense around these charts), and
// the worst case is a stable axis one step too tall, never a moving one.

const marks = new Map();

// Smallest "nice" axis ceiling >= value (1/2/2.5/5 x 10^n), so recharts'
// default five ticks land on round numbers.
export const niceCeil = (value) => {
  if (!Number.isFinite(value) || value <= 0) return null;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const fraction = value / magnitude;
  const nice =
    fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : 5;
  return (fraction > 5 ? 10 : nice) * magnitude;
};

// Returns the pinned axis maximum for this chart key, raising the high-water
// mark first when the current data exceeds it. Null means "no data seen yet
// for this key" — the caller falls back to recharts' automatic domain, so a
// chart that has never drawn anything is not pinned to an arbitrary scale.
export const stableAxisMax = (key, currentMax) => {
  const prev = marks.get(key) ?? 0;
  const next = Math.max(
    prev,
    Number.isFinite(currentMax) && currentMax > 0 ? currentMax : 0,
  );
  if (next > prev) marks.set(key, next);
  return next > 0 ? niceCeil(next) : null;
};

export const stableAxisKey = ({
  contextKey = "",
  chartId,
  dimension,
  consolidated,
}) =>
  `${contextKey}::${chartId}|${dimension}|${
    consolidated ? "consolidated" : "per-entity"
  }`;

// Test seam; production code never needs a reset because the context key
// rotates the marks out of reach instead.
export const resetStableAxes = () => {
  marks.clear();
};
